import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, FileText, Receipt, CheckCircle2, ShieldCheck } from "lucide-react";
import { SignaturePad } from "@/components/contracts/SignaturePad";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContractSignPage({ kind = "contract" }) {
  const { token } = useParams();
  const base = kind === "change" ? `${API}/change-orders/public/${token}` : `${API}/contracts/public/${token}`;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [signature, setSignature] = useState(null);
  const [signing, setSigning] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(base)
      .then(r => { setData(r.data); setName(r.data.owner_name || ""); })
      .catch(e => setError(e.response?.data?.detail || "This link is invalid"));
  }, [token, base]);

  const sign = async () => {
    if (!signature) return toast.error("Please draw your signature");
    if (!name.trim()) return toast.error("Please type your full name");
    if (!agree) return toast.error("Please confirm you agree to the terms");
    setSigning(true);
    try {
      const r = await axios.post(`${base}/sign`, { signature, signed_name: name, agree: true });
      setData(d => ({ ...d, status: "signed", signed_at: r.data.signed_at, signed_by: name }));
      setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Signing failed");
    } finally {
      setSigning(false);
    }
  };

  if (error) return <Shell><p className="text-red-600 text-center py-20" data-testid="sign-error">{error}</p></Shell>;
  if (!data) return <Shell><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div></Shell>;

  const signed = done || data.status === "signed";
  const pdfUrl = `${base}/pdf`;
  const invUrl = `${base}/invoice-pdf`;
  const isChange = kind === "change";
  const displayTotal = isChange ? data.new_total : data.total;

  return (
    <Shell company={data.company_name}>
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold text-slate-900" data-testid="sign-title">{isChange ? `Change Order ${data.change_order_number}` : `Renovation Agreement ${data.contract_number}`}</h1>
        {isChange && <p className="text-slate-700 text-sm mt-1">{data.title} · to agreement {data.contract_number}</p>}
        <p className="text-slate-600 text-sm mt-1">{data.property_address}</p>
        <p className="text-slate-900 font-semibold mt-2">{isChange ? `Change: ${data.amount_delta >= 0 ? "+" : ""}$${Number(data.amount_delta || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} · Revised total: ` : "Total: "}${Number(displayTotal || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
      </div>

      {signed ? (
        <Card className="border-green-200 bg-green-50"><CardContent className="py-10 text-center" data-testid="sign-success">
          <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-3" />
          <h2 className="text-xl font-semibold text-green-800">Thank you — your {isChange ? "change order" : "agreement"} is signed</h2>
          <p className="text-sm text-green-700 mt-2">A signed copy has been emailed to you. We look forward to working with you!</p>
          <a href={pdfUrl} target="_blank" rel="noreferrer" className="inline-block mt-4 text-sm text-indigo-700 underline">Download signed agreement</a>
        </CardContent></Card>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
            <a href={pdfUrl} target="_blank" rel="noreferrer" className="flex items-center gap-3 p-4 bg-white border rounded-lg hover:border-indigo-400 transition-colors" data-testid="sign-view-contract">
              <FileText className="w-6 h-6 text-indigo-600" /><div><p className="font-medium text-sm">{isChange ? "Change Order (PDF)" : "Agreement (PDF)"}</p><p className="text-xs text-slate-500">{isChange ? "Review the change" : "Read the full contract"}</p></div>
            </a>
            {data.invoice && (
              <a href={invUrl} target="_blank" rel="noreferrer" className="flex items-center gap-3 p-4 bg-white border rounded-lg hover:border-indigo-400 transition-colors" data-testid="sign-view-invoice">
                <Receipt className="w-6 h-6 text-emerald-600" /><div><p className="font-medium text-sm">Work Order / Invoice {data.invoice.invoice_number}</p><p className="text-xs text-slate-500">Itemized scope & pricing</p></div>
              </a>
            )}
          </div>

          <div className="bg-white border rounded-lg overflow-hidden mb-5 hidden sm:block">
            <iframe title="Agreement" src={`${pdfUrl}#toolbar=0`} className="w-full" style={{ height: 520 }} />
          </div>

          <Card><CardContent className="pt-5 space-y-4">
            <h3 className="font-semibold text-slate-900">{isChange ? "Items in this change order" : "Payment schedule"}</h3>
            <ul className="text-sm text-slate-700 space-y-1">
              {isChange && (data.items || []).map((it, i) => (
                <li key={i} className="flex justify-between border-b border-dashed pb-1"><span>{it.description}</span><b>${Number(it.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</b></li>
              ))}
              {isChange && <li className="flex justify-between pt-1"><span>Payment due: {data.milestone_label}</span><b>{data.amount_delta >= 0 ? "+" : ""}${Number(data.amount_delta || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</b></li>}
              {!isChange && (data.payment_schedule || []).map((m, i) => (
                <li key={i} className="flex justify-between border-b border-dashed pb-1"><span>{String.fromCharCode(97 + i)}) {m.label}</span><b>${Number(m.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</b></li>
              ))}
            </ul>
          </CardContent></Card>

          <Card className="mt-5"><CardContent className="pt-5 space-y-4">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-indigo-600" /> Sign electronically</h3>
            <div><Label className="text-sm">Full legal name</Label><Input value={name} onChange={e => setName(e.target.value)} data-testid="sign-name" /></div>
            <SignaturePad onChange={setSignature} />
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <Checkbox checked={agree} onCheckedChange={v => setAgree(!!v)} data-testid="sign-agree" className="mt-0.5" />
              <span>{isChange ? "I approve this change order and the revised contract total. All other terms of the original agreement remain in effect." : "I have read and agree to the terms of this agreement, including Schedules A and B and the attached work order."} I understand this electronic signature is legally binding.</span>
            </label>
            <Button className="w-full h-12 text-base" onClick={sign} disabled={signing} data-testid="sign-submit">
              {signing ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <CheckCircle2 className="w-5 h-5 mr-2" />} Sign {isChange ? "Change Order" : "Agreement"}
            </Button>
          </CardContent></Card>
        </>
      )}
    </Shell>
  );
}

const Shell = ({ company, children }) => (
  <div className="min-h-screen bg-slate-100 py-8 px-4">
    <div className="max-w-2xl mx-auto">
      {company && <p className="text-center text-xs uppercase tracking-widest text-slate-500 mb-3">{company}</p>}
      {children}
    </div>
  </div>
);
